import { IDBPDatabase } from "idb";
import { IDB, IDBInterface } from "./indexedDB";
import { v7 } from "uuid";
import Time from "../../util/Time";
import { AsCache, ownerData, WithOwner } from "./IDBSchema";
import { getFromIdsPayload } from "../../schema/general";
import { IDBEntityDef } from "./generated/entityDefs";
import { PutStatusResponse, StatusResponse } from "../../schema/console/statusData";

type createBaseColumns = {
  id: string;
  createdAt: number;
  updatedAt: number;
  cachedAt: number;
  ownerId: string;
  preparer_user_id?: string;
  created_by_user_id?: string;
};

// 全DataApi共通の土台(DB接続とIDB相互参照のみ)
export abstract class IndexedDBApi {
  protected readonly IDB: () => IDB;
  protected readonly db: IDBPDatabase<IDBInterface>;

  constructor(database: IDBPDatabase<IDBInterface>, IDBGetter: () => IDB) {
    this.IDB = IDBGetter;
    this.db = database;
  }
}

// idb の IDBPObjectStore は DB 型引数(全ストアの値型マップ)に強く依存する。IDBInterface は
// 手書きストアの一部しか宣言していないため、この関数が実際に使う操作だけを構造的に要求する
// (any による全開放を避けつつ、生成ストア名(string)のトランザクションからも呼べるようにする)。
type OwnerRowsCursor = {
  readonly primaryKey: IDBValidKey;
  delete(): Promise<void>;
  continue(): Promise<OwnerRowsCursor | null>;
};
type OwnerRowsStore = {
  openCursor(): Promise<OwnerRowsCursor | null>;
  put(value: { id: string | number; ownerId: string; cachedAt: number }): Promise<IDBValidKey>;
};

// 与えられた store(呼出側トランザクション内) で「owner の既存行を全削除 → 新規行を put」する。
// 単一ストアの replaceOwnerRows と、複数ストアを1txでまとめる IDB.replaceOwnerRowsAtomic の共通処理。
// 同一トランザクションの store を渡せば、複数ストアの差し替えを原子的に行える(途中失敗で全ロールバック)。
export async function replaceOwnerRowsInStore(
  store: OwnerRowsStore,
  ownerId: string,
  rows: { id: string | number }[],
  cachedAt: number,
): Promise<void> {
  let cursor = await store.openCursor();
  while (cursor) {
    if (Array.isArray(cursor.primaryKey) && String(cursor.primaryKey.at(1)) === ownerId) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }
  for (const row of rows) {
    await store.put({ ...row, ownerId: ownerId, cachedAt: cachedAt });
  }
}

// IndexedDB へ put する組み立て済みの行。ジェネリック行のスプレッド結果はインデックスシグネチャを
// 持たず Record<string, unknown> に載らないため、意味を名前で示した unknown の別名として定義する
// (構造は各ストアの keyPath [id, ownerId] を満たすことを呼び出し側が保証する)。
export type IdbRowPayload = unknown;

// EntityDataApi の型引数の上限(全エンティティ行が満たす共通形)。
// エンティティ横断でヘテロな DataApi 一覧を扱う場面(indexedDB.ts の走査・ストア名引き等)では
// EntityDataApi<EntityRowBounds> を要素型に使う(any の代替)。
export type EntityRowBounds = { id: string | number; updatedAt?: number; deleted_at?: number | null };

// 1エンティティ=1ストアの共通実装。
// 各DataApiはこれを継承し、エンティティ固有の結合(row組み立て)処理のみを実装する。
export abstract class EntityDataApi<TBase extends EntityRowBounds> extends IndexedDBApi {
  // 対象ストアの定義(generated/entityDefs.ts)
  public abstract readonly def: IDBEntityDef;

  // ---- 共通カラム ----

  protected addCreateBaseColumns(ownerId: string): createBaseColumns {
    const currentTime = Time.Now().GetTime();
    const baseColumns: createBaseColumns = {
      id: v7(),
      createdAt: currentTime,
      updatedAt: currentTime,
      cachedAt: currentTime,
      ownerId: ownerId,
    };
    if (this.def.hasPreparer) {
      baseColumns.preparer_user_id = ownerId;
      // created_by_user_id はサーバー側では locals.user.id で補完されるが、オフライン作成行は
      // ここで埋めないと merge push 時に caches スキーマ(created_by_user_id 必須)違反の 400 となり、
      // オンライン復帰(MergeCacheData)が永久に失敗する。preparer を持つエンティティは created_by も持つ。
      baseColumns.created_by_user_id = ownerId;
    }
    return baseColumns;
  }

  protected addPatchBaseColumns() {
    return { updatedAt: Time.Now().GetTime() };
  }

  // ---- 読み取り共通処理 ----

  protected async readAll(): Promise<AsCache<WithOwner<TBase>>[]> {
    const tx = this.db.transaction(this.def.store, "readonly");
    const collected: AsCache<WithOwner<TBase>>[] = [];
    let cursor = await tx.objectStore(this.def.store).openCursor();
    while (cursor) {
      collected.push(cursor.value);
      cursor = await cursor.continue();
    }
    await tx.done;
    return collected;
  }

  protected async readByKey(id: TBase["id"], ownerId: string): Promise<AsCache<WithOwner<TBase>> | undefined> {
    const tx = this.db.transaction(this.def.store, "readonly");
    const data = await tx.objectStore(this.def.store).get([id, ownerId]);
    await tx.done;
    return data;
  }

  // 複合インデックス [ownerId, fk] を範囲スキャンして該当行を集める。
  // 全件取得→JSフィルタを避け、該当行だけをカーソルで読む。
  protected async readByIndex(indexName: string, ownerId: string, fkValue: string, filterDeleted: boolean = true): Promise<TBase[]> {
    const tx = this.db.transaction(this.def.store, "readonly");
    const range = IDBKeyRange.only([ownerId, fkValue]);
    const collected: TBase[] = [];
    let cursor = await tx.objectStore(this.def.store).index(indexName).openCursor(range);
    while (cursor) {
      if (!filterDeleted || cursor.value.deleted_at == null) {
        collected.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    await tx.done;
    return collected;
  }

  // 所有者のデータ一覧。filterDeletedがtrueなら論理削除済みを除外する。
  protected async listByOwner(ownerId: string, filterDeleted: boolean = true): Promise<TBase[]> {
    const allData = await this.readAll();
    return allData.filter((data) => data.ownerId === ownerId && (!filterDeleted || data.deleted_at == null));
  }

  protected async findById(payload: { id: TBase["id"]; ownerId: string }, filterDeleted: boolean = true): Promise<TBase | null> {
    const data = await this.readByKey(payload.id, payload.ownerId);
    if (data === undefined) {
      return null;
    }
    if (filterDeleted && data.deleted_at != null) {
      return null;
    }
    return data;
  }

  // 所有者のデータ一覧をマスタ画面等で利用するための公開ラッパー。
  public async getAllByOwner(ownerId: string, filterDeleted: boolean = true): Promise<TBase[]> {
    return await this.listByOwner(ownerId, filterDeleted);
  }

  // 整数PKマスタ向けのオフライン採番(owner内max+1)。論理削除分も含めIDは再利用しない。
  public async getNextId(ownerId: string): Promise<number> {
    const rows = await this.listByOwner(ownerId, false);
    let max = 0;
    for (const row of rows) {
      if (typeof row.id === "number" && row.id > max) {
        max = row.id;
      }
    }
    return max + 1;
  }

  // ---- 書き込み共通処理 ----

  protected async writeRow(row: IdbRowPayload) {
    const tx = this.db.transaction(this.def.store, "readwrite");
    await tx.objectStore(this.def.store).put(row);
    await tx.done;
  }

  // 所有者の既存行を全削除→新規行を一括put(原子的)。マスタの一括差替向け。
  // rowsは「差替後の全行(論理削除行も含む)」を渡す。
  // 単一ストアでも replaceOwnerRowsAtomic に委譲し、失敗時の abort(全ロールバック)を共通化する。
  public async replaceOwnerRows(ownerId: string, rows: TBase[], cachedAt: number): Promise<void> {
    await this.IDB().replaceOwnerRowsAtomic([{ store: this.def.store, rows: rows }], ownerId, cachedAt);
  }

  // 既存行への上書き保護を行わない一括put。seed投入やシード再投入用途。
  public async bulkPut(rows: TBase[], owner: ownerData, cachedAt: number): Promise<void> {
    const tx = this.db.transaction(this.def.store, "readwrite");
    const store = tx.objectStore(this.def.store);
    for (const row of rows) {
      await store.put({ ...row, ownerId: owner.ownerId, cachedAt: cachedAt });
    }
    await tx.done;
  }

  // オフラインでの新規作成。putDefaults(serverGenerate列 + defaultValue を持つ列の初期値)を
  // フォールバックとして先に展開し、入力 data → 共通カラム(id等)の順に上書きする。
  // これにより put payload に含まれない nullable 列(Tier2 で serverGenerate を外した executed_at 等)が
  // キーごと欠落して cache/merge の zod 検証で弾かれるのを防ぐ。data に値があればそちらが優先される。
  protected async createRow<TPut extends { ownerId: string }>(data: TPut): Promise<PutStatusResponse> {
    const baseColumns = this.addCreateBaseColumns(data.ownerId);
    const putData = { ...this.def.putDefaults, ...data, ...baseColumns };

    const tx = this.db.transaction(this.def.store, "readwrite");
    await tx.objectStore(this.def.store).add(putData);
    await tx.done;

    return { status: "ok", id: baseColumns.id };
  }

  // オフラインでの更新(論理削除も含む)。
  // 存在しない・削除済みの行への更新は破損レコードを作らないため拒否する。
  protected async patchRow<TPatch extends { id: TBase["id"]; ownerId: string }>(data: TPatch): Promise<StatusResponse> {
    const original = await this.findById(data);
    if (!original) {
      throw new Error(`${this.def.store}に更新対象のデータがありません。`);
    }
    await this.writeRow({ ...original, ...data, ...this.addPatchBaseColumns() });
    return { status: "ok" };
  }

  // setDelete payload({id, deleted})を deleted_at へ変換して論理削除/復旧する。
  // delete payload をそのまま patchRow すると deleted 列が混入し deleted_at が更新されないため、ここで変換する。
  // patchRow は削除済み行を拒否するため、復元(deleted_at:null に戻す)には使えない。
  // 復元・カスケード用の deleted_at 直接書き換えは writeDeletedAt を経由する。
  protected async softDelete(data: { id: TBase["id"]; ownerId: string; deleted: boolean }): Promise<StatusResponse> {
    if (!data.deleted) {
      await this.assertRestorable(data.id, data.ownerId);
    }
    const deletedAt = data.deleted ? Time.Now().GetTime() : null;
    return await this.writeDeletedAt(data.id, data.ownerId, deletedAt);
  }

  // 復元(deleted_at→null)がオフライン起点で行えるかを検査する。復元はオンライン set-delete 専用の
  // 挙動(削除優先 の merge は復元を適用しない)であるため、既にサーバへ push 済みのトンボストーン
  // (updatedAt < cachedAt)を復元しても merge で捨てられ、後日サイレントに削除済みへ巻き戻ってしまう。
  // オフライン中に自分で削除してまだ push していない行(dirty なトンボストーン)は、サーバ上ではまだ
  // 生存しているため merge で普通に通り、復元を許可してよい。
  protected async assertRestorable(id: TBase["id"], ownerId: string): Promise<void> {
    const original = await this.readByKey(id, ownerId);
    if (original == null) {
      return;
    }
    const isSyncedTombstone = original.deleted_at != null && (original.updatedAt ?? 0) < original.cachedAt;
    if (isSyncedTombstone) {
      throw new Error("オフラインのため削除済みデータの復元はできません。オンラインに復帰してから操作してください。");
    }
  }

  // 削除済み行を含めて対象行を読み、deleted_at のみを直接書き換える。
  // patchRow は「存在しない・削除済みの行への更新を拒否する」仕様(不正な部分更新の防止)のため、
  // 論理削除済み行の復元(deleted_at:null に戻す)や、削除カスケードでの deleted_at 書き込みには使えない。
  // 復元・削除どちらの方向でも「対象行は必ず存在する」ことを前提とし、無ければ例外にする。
  protected async writeDeletedAt(id: TBase["id"], ownerId: string, deletedAt: number | null): Promise<StatusResponse> {
    const original = await this.findById({ id, ownerId }, false);
    if (!original) {
      throw new Error(`${this.def.store}に更新対象のデータがありません。`);
    }
    await this.writeRow({ ...original, deleted_at: deletedAt, ...this.addPatchBaseColumns() });
    return { status: "ok" };
  }

  // ---- Api.ts entities.putRow/patchRow/setDelete のオフラインフォールバック用公開窓口 ----
  // 既存の手書きDataApi(put/patch/delete)と命名が衝突しないよう offline* とする。
  // hasMerge:true のエンティティ(merge対応トランザクション)専用。マスタ等 hasMerge:false は呼ばない。

  public async offlinePut<TPut extends { ownerId: string }>(data: TPut): Promise<PutStatusResponse> {
    return await this.createRow(data);
  }

  public async offlinePatch<TPatch extends { id: TBase["id"]; ownerId: string }>(data: TPatch): Promise<StatusResponse> {
    return await this.patchRow(data);
  }

  public async offlineSetDelete(data: { id: TBase["id"]; ownerId: string; deleted: boolean }): Promise<StatusResponse> {
    return await this.softDelete(data);
  }

  // カスケード削除/復元用の公開窓口。コホート(親子で同一の削除時刻を共有)のため、
  // 時刻を呼出側(親側)から明示的に渡せる形にしている。
  public async offlineSetDeleteAt(id: TBase["id"], ownerId: string, deletedAt: number | null): Promise<StatusResponse> {
    return await this.writeDeletedAt(id, ownerId, deletedAt);
  }

  // ---- キャッシュ共通処理 ----

  // サーバーから取得したデータをキャッシュする。
  public async cacheAll(allData: TBase[], owner: ownerData, cachedAt: number) {
    const tx = this.db.transaction(this.def.store, "readwrite");
    const store = tx.objectStore(this.def.store);
    for (const data of allData) {
      const existing = await store.get([data.id, owner.ownerId]);
      if (this.canOverwriteByServerData(existing, data)) {
        await store.put({ ...data, ownerId: owner.ownerId, cachedAt: cachedAt });
      }
    }
    await tx.done;
  }

  // 全件スナップショット同期用: cacheAll に加えて、サーバー一覧に存在しない手元のキャッシュ行を
  // 論理削除扱いへ落とす(削除のトゥームストーン伝播)。サーバーの全件GETは deleted 行を返さないため、
  // 「一覧に無い＝サーバーで削除済み」とみなせる(全件スナップショットである前提)。
  // 未送信のオフライン編集/新規(dirty: updatedAt >= cachedAt)は誤削除しないためスキップする。
  public async cacheAllAndReconcileMissing(allData: TBase[], owner: ownerData, cachedAt: number) {
    await this.cacheAll(allData, owner, cachedAt);
    const aliveIds = new Set<TBase["id"]>(allData.map((data) => data.id));
    const tx = this.db.transaction(this.def.store, "readwrite");
    const store = tx.objectStore(this.def.store);
    let cursor = await store.openCursor();
    while (cursor) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- idb の DB 型マップは全ストアを宣言していないため、cursor.value をこのストアの行型へ写す境界
      const row = cursor.value as AsCache<WithOwner<TBase>>;
      if (row.ownerId === owner.ownerId && !aliveIds.has(row.id) && row.deleted_at == null) {
        const rowIsDirty = (row.updatedAt ?? 0) >= row.cachedAt;
        if (!rowIsDirty) {
          await cursor.update({ ...row, deleted_at: cachedAt, cachedAt: cachedAt });
        }
      }
      cursor = await cursor.continue();
    }
    await tx.done;
  }

  // 未送信のオフライン編集(updatedAt >= cachedAt)を、古いサーバーデータで上書きして消さないための判定
  private canOverwriteByServerData(existing: AsCache<WithOwner<TBase>> | undefined, incoming: TBase): boolean {
    if (existing === undefined) {
      return true;
    }
    const existingIsDirty = (existing.updatedAt ?? 0) >= existing.cachedAt;
    const incomingIsNewer = (incoming.updatedAt ?? 0) >= (existing.updatedAt ?? 0);
    return !existingIsDirty || incomingIsNewer;
  }

  public async getCache(): Promise<AsCache<WithOwner<TBase>>[]> {
    return await this.readAll();
  }

  // merge push に成功した行の cachedAt を uploadTime へ更新する(1トランザクション)。
  // これにより dirty(updatedAt >= cachedAt)が解消され、次回の全件スナップショット reconcile で
  // 削除優先 で敗れた行やユニーク吸収された行が正しく トゥームストーン 化される。
  public async markPushed(ids: { id: TBase["id"]; ownerId: string }[], cachedAt: number): Promise<void> {
    const tx = this.db.transaction(this.def.store, "readwrite");
    const store = tx.objectStore(this.def.store);
    for (const target of ids) {
      const existing = await store.get([target.id, target.ownerId]);
      if (existing) {
        await store.put({ ...existing, cachedAt: cachedAt });
      }
    }
    await tx.done;
  }

  public async clearCache() {
    const tx = this.db.transaction(this.def.store, "readwrite");
    await tx.objectStore(this.def.store).clear();
    await tx.done;
  }

  // ログイン中でないユーザーのキャッシュを削除する
  public async removeInActiveCaches(activeUsers: getFromIdsPayload) {
    const tx = this.db.transaction(this.def.store, "readwrite");
    let cursor = await tx.objectStore(this.def.store).openCursor();
    while (cursor) {
      if (Array.isArray(cursor.primaryKey)) {
        const ownerId = String(cursor.primaryKey.at(1));
        // アクティブなユーザのデータでなければ削除
        if (!activeUsers.ids.includes(ownerId)) {
          await cursor.delete();
        }
      } else {
        // キーが複合キーでないのは破損データのため問答無用で削除
        await cursor.delete();
      }
      cursor = await cursor.continue();
    }
    await tx.done;
  }
}
